/**
 * Settings gear → Update: force the browser onto the newest deploy.
 *
 * Best-practice ladder for stuck PWA / HTTP caches after Railway push:
 * 1. Probe `registration.update()` (may install a waiting worker)
 * 2. If waiting: `SKIP_WAITING` + await `controllerchange` (bounded; same timeout as
 *    SW release) — proven activation before nuclear unregister+navigate.
 *    On activation timeout, the nuclear path below still runs.
 * 3. Bust HTTP disk cache for shell URLs (`cache: 'reload'`)
 * 4. Clear Cache Storage + IndexedDB + deploy latches + unregister SWs
 * 5. Hard navigate with `_pwa` cache-bust so index.html is not served stale
 *
 * Note: this cannot change API response bodies from an old backend deploy.
 * Server code only updates when that service is redeployed from git.
 */

import { suspendDirtyLeaveForNavigation } from '../../shared/ui/dirtyLeaveHistoryPatch.js';
import { isAdminCriticalFlowActive } from './adminCriticalFlowGate.js';
import { bustAdminHttpShellCaches } from './bustAdminHttpShellCaches.js';
import { runLiveAdminPwaRefreshEscalationStep } from './adminPwaRefreshEscalation.js';
import { waitForServiceWorkerControllerChange } from './clearAdminPwaClientState.js';

export type ForceAdminAppUpdateOutcome =
  | 'started'
  | 'blocked_critical_flow'
  | 'offline';

async function probeServiceWorkerUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration === undefined) {
      return;
    }
    await registration.update();
    if (registration.waiting !== null) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Bounded wait for proven activation; nuclear clear+navigate still runs on timeout.
      await waitForServiceWorkerControllerChange();
    }
  } catch {
    // Hard clear below still recovers stuck shells.
  }
}

/**
 * User-initiated update from AdminSettingsSheet.
 * Always attempts a cache-bust replace after clearing SW/caches.
 */
export async function forceAdminAppUpdateFromSettings(): Promise<ForceAdminAppUpdateOutcome> {
  if (isAdminCriticalFlowActive()) {
    return 'blocked_critical_flow';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'offline';
  }

  suspendDirtyLeaveForNavigation();
  await probeServiceWorkerUpdate();
  await bustAdminHttpShellCaches();
  await runLiveAdminPwaRefreshEscalationStep('cache_bust_replace');
  return 'started';
}
